"use client";

import * as React from "react";
import { cn } from "@/lib/utils";

const MOBILE_NAVBAR_BREAKPOINT_PX = 1024;
const NAVBAR_HEIGHT_SMALL_PX = 48;
const NAVBAR_HEIGHT_LARGE_PX = 64;
const TOP_VISIBLE_THRESHOLD_PX = 16;
const HIDE_AFTER_SCROLL_PX = 96;
const SCROLL_DIRECTION_THRESHOLD_PX = 8;
const BOTTOM_SHEET_OPEN_ATTRIBUTE = "data-mobile-bottom-sheet-open";
const NAVBAR_HEIGHT_CSS_VAR = "--mobile-navbar-height";
const NAVBAR_OFFSET_CSS_VAR = "--mobile-navbar-offset";

function isMobileNavbarViewport() {
  return (
    typeof window !== "undefined" &&
    window.innerWidth < MOBILE_NAVBAR_BREAKPOINT_PX
  );
}

function getVisibleNavbarHeightPx() {
  if (typeof window === "undefined") return NAVBAR_HEIGHT_SMALL_PX;
  return window.innerWidth >= 640
    ? NAVBAR_HEIGHT_LARGE_PX
    : NAVBAR_HEIGHT_SMALL_PX;
}

function setDocumentNavbarOffset(isHidden: boolean) {
  if (typeof document === "undefined") return;
  const visibleHeight = getVisibleNavbarHeightPx();
  const offset = isHidden ? 0 : visibleHeight;

  document.documentElement.style.setProperty(
    NAVBAR_HEIGHT_CSS_VAR,
    `${visibleHeight}px`,
  );
  document.documentElement.style.setProperty(
    NAVBAR_OFFSET_CSS_VAR,
    `${offset}px`,
  );
}

function clearDocumentNavbarOffset() {
  if (typeof document === "undefined") return;
  document.documentElement.style.removeProperty(NAVBAR_HEIGHT_CSS_VAR);
  document.documentElement.style.removeProperty(NAVBAR_OFFSET_CSS_VAR);
}

function getOverlayMode(): "bottom-sheet" | "blocking" | null {
  if (typeof document === "undefined") return null;
  const root = document.documentElement;
  const body = document.body;

  if (
    root.hasAttribute(BOTTOM_SHEET_OPEN_ATTRIBUTE) ||
    body.hasAttribute(BOTTOM_SHEET_OPEN_ATTRIBUTE)
  ) {
    return "bottom-sheet";
  }

  if (
    root.hasAttribute("data-scroll-locked") ||
    body.hasAttribute("data-scroll-locked") ||
    root.hasAttribute("data-mobile-navbar-overlay") ||
    body.hasAttribute("data-mobile-navbar-overlay")
  ) {
    return "blocking";
  }

  return null;
}

export default function MobileAutoHideNavbar({
  children,
}: {
  children: React.ReactNode;
}) {
  const [hidden, setHidden] = React.useState(false);
  const lastScrollYRef = React.useRef(0);
  const frameRef = React.useRef<number | null>(null);
  // 직전 동기화가 오버레이(스크롤 잠금/하단 시트) 상태였는지 추적한다. 오버레이가
  // 닫히는 순간 스크롤 위치 복원(window.scrollTo)이 만들어내는 가짜 하향 delta로
  // 네비바가 잘못 숨겨지는 것을 막기 위함이다.
  const wasOverlayRef = React.useRef(false);

  const applyHiddenState = React.useCallback((nextHidden: boolean) => {
    setDocumentNavbarOffset(nextHidden);
    setHidden((current) => (current === nextHidden ? current : nextHidden));
  }, []);

  React.useEffect(() => {
    const syncNavbarState = () => {
      frameRef.current = null;
      const currentY = Math.max(window.scrollY, 0);

      if (!isMobileNavbarViewport()) {
        lastScrollYRef.current = currentY;
        wasOverlayRef.current = false;
        applyHiddenState(false);
        return;
      }

      const overlayMode = getOverlayMode();
      if (overlayMode) {
        lastScrollYRef.current = currentY;
        wasOverlayRef.current = true;
        applyHiddenState(overlayMode === "bottom-sheet");
        return;
      }

      // 오버레이가 막 닫힌 직후 프레임: 잠금 동안 body가 position:fixed라 window.scrollY가
      // 0으로 고정돼 lastScrollYRef도 0이 돼 있다. 복원된 실제 스크롤로 기준만 다시 잡고
      // 빠져나가, 가짜 delta로 네비바가 숨겨지지 않게 한다(현재 표시 상태 유지).
      // 단, 페이지 맨 위(TOP_VISIBLE_THRESHOLD_PX 이내)라면 하단 시트가 강제로 숨겼던 네비바를
      // 바로 되살린다 — 맨 위에서는 위로 스크롤할 여지가 없어 숨김 상태가 영영 풀리지 않기 때문
      // (커뮤니티 게시판 dock 더보기 시트처럼 목록 맨 위에서 여는 시트에서 재현, JCG-3029).
      if (wasOverlayRef.current) {
        wasOverlayRef.current = false;
        lastScrollYRef.current = currentY;
        if (currentY <= TOP_VISIBLE_THRESHOLD_PX) applyHiddenState(false);
        return;
      }

      const delta = currentY - lastScrollYRef.current;

      if (currentY <= TOP_VISIBLE_THRESHOLD_PX) {
        lastScrollYRef.current = currentY;
        applyHiddenState(false);
        return;
      }

      if (Math.abs(delta) < SCROLL_DIRECTION_THRESHOLD_PX) return;

      lastScrollYRef.current = currentY;
      applyHiddenState(delta > 0 && currentY > HIDE_AFTER_SCROLL_PX);
    };

    const requestSync = () => {
      if (frameRef.current !== null) return;
      frameRef.current = window.requestAnimationFrame(syncNavbarState);
    };

    lastScrollYRef.current = Math.max(window.scrollY, 0);
    syncNavbarState();
    window.addEventListener("scroll", requestSync, { passive: true });
    window.addEventListener("resize", requestSync);

    const observer = new MutationObserver(requestSync);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: [
        BOTTOM_SHEET_OPEN_ATTRIBUTE,
        "data-scroll-locked",
        "data-mobile-navbar-overlay",
      ],
    });
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: [
        BOTTOM_SHEET_OPEN_ATTRIBUTE,
        "data-scroll-locked",
        "data-mobile-navbar-overlay",
      ],
    });

    return () => {
      window.removeEventListener("scroll", requestSync);
      window.removeEventListener("resize", requestSync);
      observer.disconnect();
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
      }
      clearDocumentNavbarOffset();
    };
  }, [applyHiddenState]);

  return (
    <nav
      aria-label="주요 메뉴"
      data-mobile-auto-hide-navbar
      data-state={hidden ? "hidden" : "visible"}
      onFocusCapture={() => applyHiddenState(false)}
      className={cn(
        "sticky top-0 z-50 flex h-12 w-full items-center justify-between gap-2 bg-background/80 px-3 backdrop-blur-sm transition-transform duration-200 ease-out will-change-transform motion-reduce:transition-none sm:h-16 sm:justify-center sm:gap-8 sm:px-6 lg:container lg:mx-auto",
        hidden ? "-translate-y-full pointer-events-none" : "translate-y-0",
      )}
    >
      {children}
    </nav>
  );
}
